// EmailModal.tsx
/** @jsxImportSource theme-ui */
import React, { useState } from "react";
import { Box, Button, Input, Text } from "theme-ui";
import apiInvocation from "../api/apiInvocation";
import {message} from "antd";

interface EmailModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const EmailModal: React.FC<EmailModalProps> = ({ isOpen, onClose }) => {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = () => {
        if (isLoading || !email.trim()) return; // Prevent double submit

        setIsLoading(true);
        apiInvocation.inviteNewUser(email)
            .then((response) => {
                setEmail("");
                message.success(response.message);
                onClose();
            })
            .catch((err) => {
                message.error("Error with sending invitation");
                onClose();
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    if (!isOpen) return null;

    return (
        <Box
            sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 1000,
                padding: [3, 4],
            }}
            onClick={onClose}
        >
            <Box
                sx={{
                    backgroundColor: "background",
                    borderRadius: 3,
                    padding: [4, 5, 6],
                    width: ["calc(100% - 32px)", "90%", "400px"],
                    maxWidth: "400px",
                    minHeight: ["200px", "250px", "300px"],
                    position: "relative",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                    margin: "auto",
                    maxHeight: ["60vh", "auto"],
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Close button with X icon */}
                <Button
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        top: [2, 3],
                        right: [2, 3],
                        width: ["28px", "32px"],
                        height: ["28px", "32px"],
                        borderRadius: "50%",
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        color: "#666",
                        border: "1px solid rgba(0, 0, 0, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        padding: 0,
                        transition: "all 0.2s ease",
                        "&:hover": {
                            backgroundColor: "#f0f0f0",
                            transform: "scale(1.1)",
                        },
                    }}
                >
                    <Text sx={{ fontSize: ["14px", "16px"], fontWeight: "bold", lineHeight: 1 }}>
                        X
                    </Text>
                </Button>

                <Text
                    sx={{
                        fontSize: [5, 6, 7],
                        fontWeight: "medium",
                        mb: [6, 7, 8],
                        textAlign: "center",
                        color: "text",
                        pr: 4,
                        mt: [2, 3, 4],
                    }}
                >
                    Invite New User
                </Text>

                {/* Email Input */}
                <Box sx={{ mb: [7, 8, 9] }}>
                    <Input
                        sx={{
                            width: "100%",
                            height: ["38px", "40px", "42px"],
                            padding: [2, 3],
                            borderRadius: 6,
                            border: "1px solid #ccc",
                            fontSize: [1, 2, 3],
                            "&:focus": {
                                outline: "none",
                                borderColor: "primaryButton",
                            },
                        }}
                        type="email"
                        placeholder="Enter user email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === "Enter") {
                                handleSubmit();
                            }
                        }}
                        autoFocus
                        disabled={isLoading}
                    />
                </Box>

                {/* Submit Button */}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Button
                        sx={{
                            variant: "buttons.primary",
                            width: ["60%", "55%", "50%"],
                            fontSize: [2, 3, 4],
                            fontWeight: "bold",
                            px: [3, 4],
                            py: [2, 3],
                            height: ["38px", "40px", "42px"],
                            mt: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: isLoading ? "not-allowed" : "pointer",
                            transition: "all 0.2s ease",
                            opacity: isLoading || !email.trim() ? 0.7 : 1,
                            "&:hover": {
                                transform: isLoading ? "none" : ["none", "scale(1.02)"],
                            },
                            "&:active": {
                                transform: isLoading ? "none" : "scale(0.98)",
                            },
                        }}
                        onClick={handleSubmit}
                        disabled={!email.trim() || isLoading}
                    >
                        {isLoading ? (
                            <Box
                                sx={{
                                    width: "18px",
                                    height: "18px",
                                    border: "2px solid transparent",
                                    borderTopColor: "currentColor",
                                    borderRightColor: "currentColor",
                                    borderRadius: "50%",
                                    animation: "spin 0.8s linear infinite",
                                    "@keyframes spin": {
                                        "0%": { transform: "rotate(0deg)" },
                                        "100%": { transform: "rotate(360deg)" }
                                    }
                                }}
                            />
                        ) : (
                            "Submit"
                        )}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default EmailModal;